<template>
	<div class="grid-flex-container">
		<v-card-text>
			<v-data-table
				:footer-props="{
					itemsPerPageOptions: [10, 15, 20, 25, 30, 50, 100],
					showFirstLastPage: true,
					showCurrentPage: true,
					pagination: {
						page: 1,
						itemsPerPage: 50,
						pageStart,
						pageStop,
						pageCount,
						itemsLength: historyDataCount,
					},
				}"
				:options.sync="options"
				:server-items-length="totalCount"
				:hide-default-footer="loadingData"
				hide-default-header
				fixed-header
				dense
			>
				<template #header>
					<thead class="v-data-table-header">
						<tr>
							<th
								v-for="header in tableColumns"
								:key="header.FieldName"
							>
								{{ header.Label }}
							</th>
							<th class="fixed-col"></th>
						</tr>
						<tr v-if="loadingData" class="v-data-table__progress">
							<th :colspan="tableColumns.length" class="column">
								<v-progress-linear
									indeterminate
								></v-progress-linear>
							</th>
						</tr>
					</thead>
				</template>

				<template #body>
					<tbody v-if="!loadingData">
						<tr
							v-for="(item, id) in historyData"
							:key="item.ActionId"
						>
							<td
								v-for="column in tableColumns"
								:key="column.FieldName"
								:class="hasChanged(id, column.FieldName)"
							>
								<template
									v-if="column.FieldName == 'ActionDate'"
									>{{
										getDateTimeFormat(item.ActionDate)
									}}</template
								>
								<template
									v-else-if="column.FieldName == 'IrisUserId'"
								>
									{{ getUserDisplay(item[column.FieldName]) }}
								</template>
								<template
									v-else-if="column.FieldName == 'Deleted'"
								>
									{{
										String(
											item[column.FieldName]
										)[0].toUpperCase() +
										String(item[column.FieldName]).slice(1)
									}}
								</template>
								<template
									v-else-if="
										column.FieldName == 'IrisProcess'
									"
								>
									{{ getProcessName(item[column.FieldName]) }}
								</template>
								<template
									v-else-if="
										column.DataField.DataType ===
											fieldDataTypes.FILE &&
										item[column.FieldName + '_FileName']
									"
								>
									<v-icon class="mr-2" color="primary"
										>mdi-file-outline</v-icon
									>
									{{ item[column.FieldName + '_FileName'] }}
								</template>
								<template
									v-else-if="
										column.DataField.DataType ===
											fieldDataTypes.DATETIME ||
										column.DataField.DataType ===
											fieldDataTypes.AUTOGENERATED_DATETIME
									"
								>
									{{
										getDateTimeFormat(
											item[column.FieldName]
										)
									}}
								</template>
								<template
									v-else-if="
										column.DataField.DataType ===
										fieldDataTypes.DATE
									"
								>
									{{ getDateFormat(item[column.FieldName]) }}
								</template>
								<template v-else>
									{{ item[column.FieldName] }}
								</template>
							</td>
						</tr>
					</tbody>
				</template>
			</v-data-table>
		</v-card-text>
		<v-card-actions>
			<v-spacer />
			<v-btn text outlined @click="closeEdit()">Close</v-btn>
		</v-card-actions>
	</div>
</template>

<script>
import { FieldDataTypes } from '@/enums/fieldDataTypes';
import { mapGetters } from 'vuex';
import {
	GridsPageGetters,
	GridsRecordHistoryGetters,
} from '../../../store/getters';
import { GridsRecordHistoryActions } from '../../../store/actions';
import projectSettingsMixin from '../../../mixins/projectSettingsMixin';
import { processEnum } from '../../../enums/processEnum';

export default {
	name: 'GridRecordHistory',
	props: {
		type: String,
	},
	mixins: [projectSettingsMixin],
	data() {
		return {
			loadingData: false,
			options: {
				itemsPerPage: 50,
				page: 1,
			},
		};
	},
	watch: {
		options() {
			this.initItemHistory();
		},
	},
	computed: {
		...mapGetters({
			visibleColumns: [GridsPageGetters.VISIBLE_COLUMNS],
			gridView: [GridsPageGetters.GRID_VIEW],
			historyData: [GridsRecordHistoryGetters.GRID_DATA],
			historyDataCount: [GridsRecordHistoryGetters.GRID_DATA_COUNT],
			irisUsersMetadata: [GridsRecordHistoryGetters.IRIS_USER_METADATA],
		}),
		fieldDataTypes() {
			return FieldDataTypes;
		},
		totalCount() {
			return this.historyData.length;
		},
		pageStart() {
			return this.options.itemsPerPage * (this.options.page - 1);
		},
		pageStop() {
			return this.options.itemsPerPage * this.options.page;
		},
		pageCount() {
			if (!this.options.itemsPerPage) return 0;
			if (!this.totalCount) return 0;
			let quotient = Math.floor(
				this.totalCount / this.options.itemsPerPage
			);
			let remainder = this.totalCount % this.options.itemsPerPage;
			if (remainder === 0) return quotient;
			return quotient + 1;
		},
		visibleColumns() {
			if (!this.gridView) return [];
			let columns = JSON.parse(JSON.stringify(this.gridView.Columns));
			return columns.sort((a, b) => a.Order - b.Order);
		},

		tableColumns() {
			if (!this.gridView) return [];
			return [
				{
					Label: 'Action date',
					FieldName: 'ActionDate',
				},
				{
					Label: 'Iris User',
					FieldName: 'IrisUserId',
				},
				{
					Label: 'Deleted',
					FieldName: 'Deleted',
				},
				{
					Label: 'Process',
					FieldName: 'IrisProcess',
				},
				...this.visibleColumns,
			];
		},
	},

	destroyed() {
		this.$store.dispatch(GridsRecordHistoryActions.CLEAR);
	},
	methods: {
		hasChanged(id, column) {
			if (column === 'ActionDate' || column === 'IrisUserId') return '';

			if (this.historyData.length > id + 1) {
				let newColumn = this.historyData[id][column];
				let oldColumn = this.historyData[id + 1][column];
				if (newColumn !== oldColumn) {
					return 'changed';
				}
			}
			return '';
		},
		initItemHistory() {
			this.loadingData = true;
			let requestData = {
				EntityId: this.gridView.EntityId,
				RecordId: this.$route.params.assetId,
				UserId: null,
				RowsPerPage: this.options.itemsPerPage,
				Page: this.options.page,
				StartDate: null,
				EndDate: null,
				QuickFilter: null,
				AdvancedFilter: null,
			};
			this.$store
				.dispatch(GridsRecordHistoryActions.LOAD_GRID_DATA, requestData)
				.then(() => {
					this.loadingData = false;
				});
		},
		closeEdit() {
			this.$router.go(-1);
			// this.$router.push(this.$route.path.split(`/${this.type}`)[0]);
		},
		getUserDisplay(userId) {
			var user = this.irisUsersMetadata.find((x) => x.Id == userId);
			if (user) {
				return user.IrisUserFormat;
			}
			return userId;
		},
		getProcessName(value) {
			switch (value) {
				case processEnum.None:
					return 'None';
				case processEnum.InventoryForm:
					return 'InventoryForm';
				case processEnum.EditGrid:
					return 'EditGrid';
				case processEnum.DataImport:
					return 'DataImport';
				case processEnum.DebugInitialDataSeed:
					return 'DebugInitialDataSeed';
			}
		},
	},
};
</script>

<style lang="scss">
.grid-flex-container {
	.v-card__text {
		overflow-y: scroll;

		.changed {
			background-color: #ffc00030;
		}

		.grid-field {
			padding: 0 1.5rem;
			margin: 1.5rem 0;
		}

		.v-input.v-input--is-readonly {
			padding: 0 1.5rem;
			margin: 1.5rem 0;
		}
	}
}
</style>
